import { CheckIcon, LoaderCircleIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import type { FulfillmentDTO } from '@invenco/sales-interface/interfaces';
import type { ShipmentDTO } from '@invenco/shipping-interface';

import { Button } from '@/components/core/button';
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/core/popover';
import {
  Timeline,
  TimelineEntry,
  TimelineEntryBody,
  TimelineEntryHeader,
  TimelineEntryTitle,
  TimelineMarker,
} from '@/components/timeline';
import { EMPTY_TEXT_VALUE } from '@/constants';
import { cn } from '@/shared/helpers';

import {
  deriveFulfillmentStages,
  type FulfillmentStage,
  type FulfillmentStageStatus,
} from './fulfillment-stages';

function FulfillmentStageMarker({
  index,
  status,
}: {
  index: number;
  status: FulfillmentStageStatus;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex size-5 items-center justify-center rounded-full text-xs font-medium',
        status === 'completed' && 'bg-state-success text-state-success-foreground',
        status === 'in-progress' && 'bg-state-progress text-state-progress-foreground',
        status === 'not-started' && 'bg-state-inactive text-state-inactive-foreground',
      )}
    >
      {status === 'completed' ? <CheckIcon className="size-3" /> : index + 1}
    </span>
  );
}

function FulfillmentStageTimelineEntry({
  stage,
  index,
}: {
  stage: FulfillmentStage;
  index: number;
}) {
  // Completed falls through to TimelineEntry's default disc+check (matches the design's pale
  // green bead). In-progress is an unfilled spinner ring; not-started is a numbered grey bead.
  let marker: ReactNode;
  if (stage.status === 'in-progress') {
    marker = (
      <span aria-hidden className="flex size-6 items-center justify-center">
        <LoaderCircleIcon className="size-5 animate-spin text-state-progress-foreground" />
      </span>
    );
  } else if (stage.status === 'not-started') {
    marker = (
      <TimelineMarker type="inactive">
        <span className="text-xs font-medium">{index + 1}</span>
      </TimelineMarker>
    );
  }

  return (
    <TimelineEntry
      type={stage.status === 'completed' ? 'success' : undefined}
      icon={stage.status === 'completed' ? CheckIcon : undefined}
      marker={marker}
    >
      <TimelineEntryHeader>
        <TimelineEntryTitle>{stage.label}</TimelineEntryTitle>
      </TimelineEntryHeader>
      {stage.description ? <TimelineEntryBody>{stage.description}</TimelineEntryBody> : null}
    </TimelineEntry>
  );
}

function FulfillmentStageReadout({
  stages,
  currentKey,
}: {
  stages: FulfillmentStage[];
  currentKey?: FulfillmentStage['key'];
}) {
  return (
    <span className="pointer-events-none flex items-center justify-center gap-2 text-sm font-medium whitespace-nowrap">
      {stages.map((stage, index) => (
        <span
          key={stage.key}
          aria-label={`${stage.label} ${stage.status}`}
          aria-current={stage.key === currentKey ? 'step' : undefined}
          data-state={stage.status}
          className="flex shrink-0 items-center gap-2"
        >
          {index > 0 ? (
            <span aria-hidden className="h-0.5 w-4 shrink-0 rounded-full bg-border" />
          ) : null}
          <FulfillmentStageMarker index={index} status={stage.status} />
          <span
            className={cn(
              'text-sm font-medium text-foreground',
              // Narrow widths show only the current step's label; the card's detail grid widens
              // to four columns at the same breakpoint, where all labels fit.
              stage.key !== currentKey && 'sr-only @xl/main:not-sr-only',
            )}
          >
            {stage.label}
          </span>
        </span>
      ))}
    </span>
  );
}

// Shown in place of the tracker while shipments load: the same step row, every step inactive
// (grey, numbered) with none current, and non-interactive (no popover) since there's no data yet.
const PLACEHOLDER_STAGES: FulfillmentStage[] = [
  { key: 'routing', label: 'Routing', status: 'not-started' },
  { key: 'fulfillment', label: 'Fulfillment', status: 'not-started' },
  { key: 'delivery', label: 'Delivery', status: 'not-started' },
];

export function FulfillmentStagePlaceholder() {
  return (
    <div aria-hidden className="inline-flex h-8 max-w-full items-center px-2.5">
      <FulfillmentStageReadout stages={PLACEHOLDER_STAGES} />
    </div>
  );
}

export function FulfillmentStageTracker({
  fulfillment,
  shipments,
}: {
  fulfillment: FulfillmentDTO;
  shipments: ShipmentDTO[];
}) {
  const stages = deriveFulfillmentStages(shipments);
  const currentStage = stages.find((stage) => stage.status === 'in-progress') ?? stages.at(-1)!;
  const location = fulfillment.warehouseLocationName ?? fulfillment.locationName;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          aria-label={`Open fulfillment stage details for ${fulfillment.name}`}
          className="max-w-full overflow-x-auto hover:bg-muted/50 aria-expanded:bg-muted/50"
        >
          <FulfillmentStageReadout stages={stages} currentKey={currentStage.key} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <PopoverHeader>
          <PopoverTitle>{fulfillment.name}</PopoverTitle>
          <PopoverDescription>
            {location} · {fulfillment.qtyRequested ?? EMPTY_TEXT_VALUE} items
          </PopoverDescription>
        </PopoverHeader>
        <Timeline>
          {stages.map((stage, index) => (
            <FulfillmentStageTimelineEntry key={stage.key} stage={stage} index={index} />
          ))}
        </Timeline>
      </PopoverContent>
    </Popover>
  );
}
